import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const CopyLink = ({ label, url, testid }) => (
  <div className="text-xs">
    <div className="text-muted-foreground">{label}</div>
    <div className="flex gap-2 items-center">
      <code className="flex-1 break-all bg-muted rounded px-2 py-1">{url}</code>
      <Button size="sm" variant="outline" type="button" data-testid={testid}
        onClick={() => navigator.clipboard?.writeText(url).then(() => toast.success("Link kopiert"))}>Kopieren</Button>
    </div>
  </div>
);

const RoomChannel = ({ r, onReload }) => {
  const [bk, setBk] = useState(r.ical_booking);
  const [ab, setAb] = useState(r.ical_airbnb);
  const save = async () => {
    await api.patch(`/rooms/${r.room_id}/ical`, { ical_booking: bk.trim(), ical_airbnb: ab.trim() });
    toast.success(`${r.label}: Import-Links gespeichert`);
    onReload();
  };
  const regen = async () => {
    if (!window.confirm("Neuen Export-Token erzeugen? Die alten Links bei Booking.com/Airbnb funktionieren dann nicht mehr.")) return;
    await api.post(`/rooms/${r.room_id}/ical-export-token`);
    toast.success("Neuer Token erzeugt – Links bei den Kanälen ersetzen");
    onReload();
  };
  return (
    <div className="rounded-xl border p-3 space-y-2" data-testid={`channel-room-${r.slug}`}>
      <div className="font-semibold">{r.label} <span className="text-xs text-muted-foreground">({r.name})</span></div>
      <div className="text-xs text-muted-foreground">Booking.com: {r.booking} · Airbnb: {r.airbnb || "–"}</div>
      <Input value={bk} onChange={(e) => setBk(e.target.value)} placeholder="iCal-Link von Booking.com (Kalender exportieren)" data-testid={`ical-booking-${r.slug}`} />
      {r.airbnb && <Input value={ab} onChange={(e) => setAb(e.target.value)} placeholder="iCal-Link von Airbnb (Kalender exportieren)" data-testid={`ical-airbnb-${r.slug}`} />}
      <Button size="sm" type="button" onClick={save} data-testid={`ical-save-${r.slug}`}>Import-Links speichern</Button>
      <CopyLink label="Export für Booking.com („Kalender importieren“)" url={r.export_booking} testid={`export-booking-copy-${r.slug}`} />
      {r.export_airbnb && <CopyLink label="Export für Airbnb („Kalender importieren“)" url={r.export_airbnb} testid={`export-airbnb-copy-${r.slug}`} />}
      <Button size="sm" variant="ghost" type="button" onClick={regen} data-testid={`export-token-regen-${r.slug}`}>Export-Token neu erzeugen</Button>
    </div>
  );
};

export const ChannelsCard = () => {
  const [d, setD] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const load = () => api.get("/channels").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/ical/sync");
      toast.success(`Abgleich fertig: ${data.created} neu, ${data.updated} geändert`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Abgleich fehlgeschlagen"); }
    finally { setSyncing(false); }
  };
  if (!d) return null;
  return (
    <div className="msb-card space-y-3" data-testid="channels-card">
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-bold flex-1" style={{ fontFamily: "'Work Sans',sans-serif" }}>Kanäle & Kalender (iCal)</h2>
        <Button size="sm" variant="outline" onClick={sync} disabled={syncing} data-testid="ical-sync-now-btn">{syncing ? "Gleiche ab…" : "Jetzt abgleichen"}</Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Import alle 15 Minuten. Ferienwohnung komplett = Doppelzimmer + Einzelzimmer + Einzelwohnzimmer.
        {d.last_sync ? ` Letzter Abgleich: ${new Date(d.last_sync.at).toLocaleString("de-DE")}` : ""}
      </p>
      <div className="grid lg:grid-cols-2 gap-3">
        {d.rooms.map((r) => <RoomChannel key={r.slug} r={r} onReload={load} />)}
      </div>
    </div>
  );
};
